import React, { useCallback, useContext, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Fade,
    Grid,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import { useSnackbar } from 'notistack';
import * as yup from 'yup';
import { Formik, Form } from 'formik';
import { AuthContext } from '../../context/auth';
import { getDataFull, postData, putData, deleteData } from '../../services/ajax';
import { AlertModalContext } from '../../components/modal/alert';
import { LoadingModalContext } from '../../components/modal/loading';
import MaterialTable, { DataType } from '../../components/material-table';
import { blue, green, red } from '@mui/material/colors';

const validationSchema = yup.object({
    nombre: yup
        .string()
        .trim()
        .required('El nombre es obligatorio')
        .max(255, 'Máximo 255 caracteres'),
});

function DeterminacionGrupoPage() {
    const theme = useTheme();
    const { usuario } = useContext(AuthContext);
    const { enqueueSnackbar } = useSnackbar();
    const alertModal = useContext(AlertModalContext);
    const loadingModal = useContext(LoadingModalContext);

    const puedeGestionar = usuario?.nivel_usuario === 1 || usuario?.sup_usuarios === true;

    const [rowsTableMain, setRowsTableMain] = useState(null);
    const [tableMainNeedUpdate, setTableMainNeedUpdate] = useState(Date.now());

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const openNuevo = () => {
        setEditing(null);
        setDialogOpen(true);
    };

    const openEditar = (row) => {
        setEditing(row);
        setDialogOpen(true);
    };

    const cerrarDialog = () => {
        setDialogOpen(false);
        setEditing(null);
    };

    const onSubmitForm = async (values, { setSubmitting, resetForm }) => {
        loadingModal.setOpen(true);
        let res;
        if (editing) {
            res = await putData(`determinacion-grupos/${editing.id}`, { nombre: values.nombre.trim() }, usuario.token);
        } else {
            res = await postData('determinacion-grupos', { nombre: values.nombre.trim() }, usuario.token);
        }
        loadingModal.setOpen(false);
        setSubmitting(false);

        if (res.error) {
            enqueueSnackbar(res.error, { variant: 'error' });
            return;
        }
        enqueueSnackbar(editing ? 'Cambios guardados' : 'Sector agregado', { variant: 'success' });
        resetForm();
        cerrarDialog();
        setTableMainNeedUpdate(Date.now());
    };

    const onEliminar = (row) => {
        alertModal.showModal(
            '¿Eliminar este sector?',
            `Se borrará "${row.nombre}". Solo podés eliminar sectores que no tengan determinaciones cargadas.`,
            async () => {
                loadingModal.setOpen(true);
                const res = await deleteData(`determinacion-grupos/${row.id}`, usuario.token);
                loadingModal.setOpen(false);
                if (res.error) enqueueSnackbar(res.error, { variant: 'error' });
                else {
                    enqueueSnackbar('Sector eliminado', { variant: 'success' });
                    setTableMainNeedUpdate(Date.now());
                }
            }
        );
    };

    const onEditarFromTable = useCallback((row) => {
        openEditar(row);
    }, []);

    const onEliminarFromTable = useCallback(
        (row) => {
            onEliminar(row);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [usuario?.token]
    );

    const tableMain = useMemo(
        () => ({
            title: 'Listado de sectores',
            idName: 'id',
            defaultOrder: 'asc',
            headers: [
                { id: 'id', label: 'Nº', dataType: DataType.NUMBER, sortable: true },
                { id: 'nombre', label: 'Nombre del sector', dataType: DataType.TEXT, sortable: true },
            ],
            loadData: async (orderBy, order, page, rowsPerPage, searchText, selectedFilter, pageUpdate, totalUpdate, filterListUpdate) => {
                setRowsTableMain(null);
                const apiPage = page + 1;
                const params = new URLSearchParams();
                params.append('per_page', rowsPerPage.toString());
                params.append('page', apiPage.toString());
                if (orderBy) params.append('order_by', orderBy);
                if (order) params.append('order', order);
                if (searchText) params.append('search_text', searchText);

                const response = await getDataFull(`determinacion-grupos?${params.toString()}`, usuario.token);

                if (response.error === '') {
                    const list = response.data || [];
                    const tablePage = response.meta?.page ? response.meta.page - 1 : page;
                    pageUpdate(tablePage);
                    totalUpdate(response.meta?.total || 0);
                    filterListUpdate([]);
                    setRowsTableMain(list);
                } else {
                    enqueueSnackbar(response.error, { variant: 'error' });
                    setRowsTableMain([]);
                }
            },
            moreMenuItems: puedeGestionar
                ? [
                      {
                          label: 'Agregar nuevo sector',
                          secondary: 'Dar de alta un sector en el catálogo',
                          icon: <AddRoundedIcon sx={{ color: green[600] }} />,
                          itemColor: green[600],
                          action: () => openNuevo(),
                      },
                  ]
                : [],
            rowActions: puedeGestionar
                ? (row) => [
                      {
                          label: 'Editar nombre',
                          secondary: 'Cambiar cómo se muestra este sector',
                          icon: <EditRoundedIcon sx={{ color: blue[600] }} />,
                          itemColor: blue[600],
                          action: () => onEditarFromTable(row),
                      },
                      {
                          label: 'Eliminar sector',
                          secondary: 'Solo si no tiene determinaciones asociadas',
                          icon: <DeleteOutlineRoundedIcon sx={{ color: red[600] }} />,
                          itemColor: red[600],
                          dividerBefore: true,
                          action: () => onEliminarFromTable(row),
                      },
                  ]
                : () => [],
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [usuario?.token, puedeGestionar, enqueueSnackbar]
    );

    const heroGradient = useMemo(
        () =>
            theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.35)} 0%, ${alpha('#1a237e', 0.5)} 50%, ${alpha(theme.palette.secondary.main, 0.25)} 100%)`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha('#e8f4fc', 1)} 45%, ${alpha(theme.palette.secondary.main, 0.15)} 100%)`,
        [theme]
    );

    return (
        <Layout nombrePagina="Sectores">
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Fade in timeout={500}>
                        <Box
                            sx={{
                                position: 'relative',
                                overflow: 'hidden',
                                borderRadius: 3,
                                p: { xs: 2, md: 3 },
                                background: heroGradient,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            }}
                        >
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                                        color: 'primary.main',
                                    }}
                                >
                                    <ScienceOutlinedIcon sx={{ fontSize: 28 }} />
                                </Box>
                                <Box flex={1}>
                                    <Typography variant="h6" fontWeight={700}>
                                        Catálogo de sectores
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Acá definís los <strong>sectores</strong> que agrupan las determinaciones de laboratorio (por área, técnica o tipo de estudio). Sirve para mantener el catálogo ordenado antes de cargar determinaciones y códigos. El listado de abajo es el maestro de esos sectores.
                                    </Typography>
                                </Box>
                                {puedeGestionar && (
                                    <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openNuevo} sx={{ borderRadius: 2, flexShrink: 0 }}>
                                        Agregar sector
                                    </Button>
                                )}
                            </Stack>
                        </Box>
                    </Fade>
                </Grid>

                <Grid size={12}>
                    <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TipsAndUpdatesRoundedIcon color="info" fontSize="small" />
                                <Typography fontWeight={600}>Cómo usar esta pantalla</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                            <Typography variant="body2" color="text.secondary" component="div">
                                <strong>Buscar:</strong> tocá la lupa en la barra gris de la tabla y escribí parte del nombre.{' '}
                                <strong>Ordenar:</strong> clic en &quot;Nº&quot; o &quot;Nombre del sector&quot;.{' '}
                                <strong>Acciones por fila:</strong> el botón con tres puntos ⋮ a la izquierda (editar o eliminar).{' '}
                                <strong>Cantidad de filas:</strong> selector inferior &quot;Filas por página&quot;.
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                </Grid>

                <Grid size={12}>
                    <Fade in timeout={400}>
                        <Box>
                            <MaterialTable
                                title={tableMain.title}
                                loadData={tableMain.loadData}
                                headers={tableMain.headers}
                                data={rowsTableMain}
                                moreMenuItems={tableMain.moreMenuItems}
                                selectable={false}
                                rowActions={puedeGestionar ? tableMain.rowActions : null}
                                idName={tableMain.idName}
                                defaultOrder={tableMain.defaultOrder}
                                defaultRowsPerPage={10}
                                needUpdate={tableMainNeedUpdate}
                                dense
                            />
                        </Box>
                    </Fade>
                </Grid>
            </Grid>

            <Dialog open={dialogOpen} onClose={cerrarDialog} fullWidth maxWidth="sm" TransitionProps={{ timeout: 400 }}>
                <DialogTitle sx={{ pb: 1 }}>{editing ? 'Editar sector' : 'Nuevo sector'}</DialogTitle>
                <Formik
                    initialValues={{ nombre: editing?.nombre || '' }}
                    validationSchema={validationSchema}
                    enableReinitialize
                    onSubmit={onSubmitForm}
                >
                    {({ isSubmitting, values, errors, touched, handleChange, handleBlur }) => (
                        <>
                            <DialogContent>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Elegí un nombre que todo el equipo entienda. Podés cambiarlo cuando quieras.
                                </Typography>
                                <Form id="form-determinacion-grupo">
                                    <TextField
                                        name="nombre"
                                        label="Nombre del sector"
                                        placeholder="Ej.: Hematología, Bioquímica general…"
                                        fullWidth
                                        variant="outlined"
                                        autoFocus
                                        value={values.nombre}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(touched.nombre && errors.nombre)}
                                        helperText={touched.nombre && errors.nombre ? errors.nombre : undefined}
                                    />
                                </Form>
                            </DialogContent>
                            <DialogActions sx={{ px: 3, pb: 2 }}>
                                <Button onClick={cerrarDialog} color="inherit">
                                    Cancelar
                                </Button>
                                <Button variant="contained" disabled={isSubmitting} size="large" type="submit" form="form-determinacion-grupo">
                                    {editing ? 'Guardar cambios' : 'Crear sector'}
                                </Button>
                            </DialogActions>
                        </>
                    )}
                </Formik>
            </Dialog>
        </Layout>
    );
}

export default DeterminacionGrupoPage;
